import { duplicateItemRemover } from "./duplicateItemRemover";
import { GeneratedFilesTypes } from "../Store/modules/streamedChat/streamedChat";

export const metadataRegexPatterns = (): {
  sessionId: RegExp;
  sessionEditedId: RegExp;
  refrences: RegExp;
  textDirection: RegExp;
  utmReplacement: RegExp;
  statusDetector: RegExp;
  bracesRemover: RegExp;
  thirdPartyError: RegExp;
  imageUrlDetector: RegExp;
  revisedPrompt: RegExp;
  needsAccountUpgrade: RegExp;
  chartRegex: RegExp;
  fileNameFromLink: RegExp;
  fileUrl: RegExp;
  videoUrl: RegExp;
  removeHtmlEscae: RegExp;
  audioUrl: RegExp;
} => {
  return {
    sessionId: /\{"session_id":"?(\d+)"?\}/, // Ex: {"session_id":4140} or {"session_id":"4140"}
    sessionEditedId: /\{"session_edited_id":("?\d+"?|null)\}/, // Ex: {"session_edited_id":"22939"} | null
    refrences: /{"references":(\[\{[\s\S]*?\}\])}/, // Ex: {"refrences":[{"...": "..."}]}
    textDirection: /^[^a-zA-Zآ-ی]*[a-zA-Z]/, // Matched pattern means ltr direction
    utmReplacement: /(?<=utm_source=)(?:[^\)]+|[^&]+|.+$|)/g, // EX: https://lamtakam.com?utm_source=openai
    statusDetector: /{"status":"(?:response.)?([^(?:\.|")]+)[^}]+}/g, // EX : {"status":"response.([^(?:\.|")]+)
    bracesRemover: /\((\[[^\)]+\))\)(\s?\.)?/g, // Replacment: $2$1
    thirdPartyError: /ThirdPartyError/, // Ex: ThirdPartyError
    imageUrlDetector: /{"image_url":"([^"]+)"}/g, // {"image_url":"..."} returns image value
    revisedPrompt: /{"revised_prompt":"([^"]+)"}/, // {"revised_prompt":"..."} returns revised prompt value
    needsAccountUpgrade: /\{"needs_account_upgrade":(true|false)\}/, // Ex: {"needs_account_upgrade":true}
    chartRegex: /```chart\s*(\[[\s\S]+?])\s*```/g,
    fileNameFromLink: /([^\/]+\.[a-zA-Z0-9]+).?$/, // This pattern needs improvement
    fileUrl: /{"file_url":"([^"]+)","file_type":"([^"]+)"}/g,
    videoUrl: /{"video_url":"([^"]+)","file_type":"([^"]+)"}/g,
    removeHtmlEscae: /^<pre><code[^>]+>([\s\S]+)<\/code><\/pre>/, // To apply HTML tags on the browser (not showing them)
    audioUrl: /{"audio_url":"([^"]+)","file_type":"(mp3|wav|ogg|m4a)"}/g,
  };
};

export const removeMetaDataFromRes = (text: string): string => {
  return text
    .replace(metadataRegexPatterns().statusDetector, "")
    .replace(metadataRegexPatterns().thirdPartyError, "")
    .replace(metadataRegexPatterns().bracesRemover, "$2$1")
    .replace(metadataRegexPatterns().sessionId, "")
    .replace(metadataRegexPatterns().sessionEditedId, "")
    .replace(metadataRegexPatterns().refrences, "")
    .replace(metadataRegexPatterns().utmReplacement, "lamtakam.com")
    .replace(metadataRegexPatterns().revisedPrompt, "")
    .replace(metadataRegexPatterns().imageUrlDetector, "")
    .replace(metadataRegexPatterns().needsAccountUpgrade, "")
    .replace(metadataRegexPatterns().fileUrl, "")
    .replace(metadataRegexPatterns().videoUrl, "")
    .replace(metadataRegexPatterns().audioUrl, "")
    .trim();
};

export const extractVideoUrl = (rawText: string): any[] => {
  const videoUrlMatch = [...rawText.matchAll(metadataRegexPatterns().videoUrl)];

  if (!videoUrlMatch.length) return [];

  return videoUrlMatch.map((m: any) => {
    try {
      return { videoLink: m[1].toString().trim(), videoType: m[2].trim() };
    } catch (e) {
      console.log("Invalid JSON:", e);
      return [];
    }
  });
};

export const extractFileUrl = (rawText: string): any[] => {
  const fileUrlMatch = [...rawText.matchAll(metadataRegexPatterns().fileUrl)];

  if (!fileUrlMatch.length) return [];

  return fileUrlMatch.map((m: any) => {
    try {
      return { fileLink: m[1].toString().trim(), fileType: m[2].trim() };
    } catch (e) {
      console.log("Invalid JSON:", e);
      return [];
    }
  });
};

export const extractFileNameFromLink = (rawText: string): string => {
  const fileNameMatch = rawText.match(metadataRegexPatterns().fileNameFromLink);
  if (fileNameMatch) {
    return fileNameMatch[1];
  }
  return "";
};

export const extractChartData = (rawText: string): any[] => {
  const matches = [...rawText.matchAll(metadataRegexPatterns().chartRegex)];
  if (!matches.length) return [];

  return matches.map((m) => {
    try {
      return JSON.parse(m[1]);
    } catch (e) {
      console.log("Invalid chart JSON:", e);
      return [];
    }
  });
};

export const extractNeedAccountUpgrade = (rawText: string): string => {
  const needsAccountUpgradeMatch = rawText.match(metadataRegexPatterns().needsAccountUpgrade);
  if (needsAccountUpgradeMatch) {
    return needsAccountUpgradeMatch[1];
  }
  return "false";
};

export const extractImageUrl = (rawText: string): any[] => {
  const matches = [...rawText.matchAll(metadataRegexPatterns().imageUrlDetector)];
  if (!matches.length) return [];

  return matches.map((m) => {
    let raw = m[1].toString().trim();
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  });
};

export const extractRevisedPrompt = (rawText: string): string => {
  const revisedPromptMatch = rawText.match(metadataRegexPatterns().revisedPrompt);

  if (revisedPromptMatch) {
    return revisedPromptMatch[1];
  }

  return "";
};

export const extractSessionId = (rawText: string): number | null => {
  const sessionMatch = rawText.match(/\{"session_id":"?(\d+)"?\}/);
  if (!sessionMatch) return null;

  const parsed = parseInt(sessionMatch[1], 10);
  return isNaN(parsed) || parsed <= 0 ? null : parsed;
};

export const extractSessionEditedId = (rawText: string): number | null => {
  const sessionMatch = rawText.match(/\{"session_edited_id":("?\d+"?|null)\}/);

  if (!sessionMatch) {
    return null;
  }

  const raw = sessionMatch[1].replace(/"/g, "");
  if (raw === "null") return null;

  const parsed = parseInt(raw, 10);
  return isNaN(parsed) || parsed <= 0 ? null : parsed;
};

export const extractStatus = (rawText: string): string[] => {
  const statusMatch: string[] = Array.from(
    rawText.matchAll(metadataRegexPatterns().statusDetector),
    (m) => m[1],
  );

  if (statusMatch) {
    return statusMatch;
  }

  return [];
};

export const extractRefrences = (rawText: string): [] => {
  // Extract JSON array
  const arrayMatch = rawText.match(metadataRegexPatterns().refrences);

  if (arrayMatch) {
    try {
      const parsedJson = JSON.parse(arrayMatch[1]);
      const unique: any = duplicateItemRemover(parsedJson, "title");
      return unique;
    } catch (e) {
      console.error("Invalid JSON array format:", e);
    }
  }

  // As default value
  return [];
};

export const getTextDirection = (text: string): "rtl" | "ltr" => {
  // To set the default dir which is RTL
  if (!text) return "rtl";

  // To handle web-search-status
  text = removeMetaDataFromRes(text);

  // To get the regex pattern
  const textDirPattern = metadataRegexPatterns().textDirection;

  // To detect the pure-text direction
  return textDirPattern.test(text) ? "ltr" : "rtl";
};

export const replaceUtmToLamtakam = (link: string): string => {
  return link.replace(metadataRegexPatterns().utmReplacement, "lamtakam.com");
};

export const makeMoneyFormat = (text: string): string => {
  const raw = text.replace(/[^\d]/g, "");
  return new Intl.NumberFormat("en-US").format(Number(raw));
};


// Used just for categorizede services, since there is no "codding"-related things
export const removeHtmlEscaeWrapper = (text: string): string => {
  const removeHtmlEscaePattern =  metadataRegexPatterns().removeHtmlEscae;
  text = removeStyleAttribute(text); // To remove styles attr
  return text.replace(removeHtmlEscaePattern, "$1").replace(/&lt;/g,"<").replace(/&gt;/g, ">");
};

const removeStyleAttribute = (text: string): string => {
  return text.replace(/style=.*?"[^"]+"/g, '');
}

type AudioMeta = {
  audioLink: string;
  audioType: string;
};

export const extractAudioUrl = (rawText: string): any[] => {
  const audioMatches = [...rawText.matchAll(metadataRegexPatterns().audioUrl)];

  if (!audioMatches.length) return [];

  return audioMatches
      .map((m): AudioMeta | null => {
        try {
          return {
            audioLink: String(m[1]).trim(),
            audioType: String(m[2]).trim(),
          };
        } catch (e) {
          console.log("Invalid audio metadata:", e);
          return null;
        }
      })
      .filter((item): item is AudioMeta => item !== null);
};